import { interpolate } from '../../global';
import { ILanguageResources, ITranslationModule } from '../../interfaces';

class Id {
  private constructor() {}

  public static readonly prefix = 'UI.scripts.v6config.SupplierServices.';
  public static readonly supplier = `${Id.prefix}supplier`;
  public static readonly supplierOfflineSince = `${Id.prefix}supplierOfflineSince`;
  public static readonly supplierOfflineUpgradingSince = `${Id.prefix}supplierOfflineUpgradingSince`;
  public static readonly supplierOfflineVersionMismatch = `${Id.prefix}supplierOfflineVersionMismatch`;
  public static readonly supplierOfflineUnknownReasonSince = `${Id.prefix}supplierOfflineUnknownReasonSince`;
  public static readonly supplierOfflineUpgradingMsg = `${Id.prefix}supplierOfflineUpgradingMsg`;
  public static readonly supplierOfflineTitle = `${Id.prefix}supplierOfflineTitle`;
  public static readonly supplierOfflineMsg = `${Id.prefix}supplierOfflineMsg`;
  public static readonly supplierFrameConfigOfflineMsg = `${Id.prefix}supplierFrameConfigOfflineMsg`;
  public static readonly frameConfigurationTitle = `${Id.prefix}frameConfigurationTitle`;
  public static readonly tacApprovalNotSetMsg = `${Id.prefix}tacApprovalNotSetMsg`;
}

export class LangSupplierServices {
  private constructor() {}

  public static supplier(): string {
    return interpolate(Id.supplier);
  }

  public static supplierOfflineSince(params: { supplierName: string; time: string; extraText: string }): string {
    return interpolate(Id.supplierOfflineSince, params);
  }

  public static supplierOfflineUpgradingSince(params: {
    supplierName: string;
    time: string;
    extraText: string;
  }): string {
    return interpolate(Id.supplierOfflineUpgradingSince, params);
  }

  public static supplierOfflineVersionMismatch(params: { supplierName: string }): string {
    return interpolate(Id.supplierOfflineVersionMismatch, params);
  }

  public static supplierOfflineUnknownReasonSince(params: {
    supplierName: string;
    time: string;
    extraText: string;
  }): string {
    return interpolate(Id.supplierOfflineUnknownReasonSince, params);
  }

  public static supplierOfflineUpgradingMsg(): string {
    return interpolate(Id.supplierOfflineUpgradingMsg);
  }

  public static supplierOfflineTitle(): string {
    return interpolate(Id.supplierOfflineTitle);
  }

  public static supplierOfflineMsg(): string {
    return interpolate(Id.supplierOfflineMsg);
  }

  public static supplierFrameConfigOfflineMsg(): string {
    return interpolate(Id.supplierFrameConfigOfflineMsg);
  }

  public static frameConfigurationTitle(): string {
    return interpolate(Id.frameConfigurationTitle);
  }

  public static tacApprovalNotSetMsg(params: { supplier: string }): string {
    return interpolate(Id.tacApprovalNotSetMsg, params);
  }
}

export class SupplierServicesModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(Id.supplier, "{{glossary 'supplier'}}", 'Glossary for supplier');
    register.add(
      Id.supplierOfflineSince,
      '{{supplierName}} is offline. Cannot connect since {{time}}. {{extraText}}',
      'Supplier offline message with time and extra text'
    );
    register.add(
      Id.supplierOfflineUpgradingSince,
      '{{supplierName}} is offline for an upgrade since {{time}}. Expected to resume within 1 hr. {{extraText}}',
      'Supplier offline for upgrade message'
    );
    register.add(
      Id.supplierOfflineVersionMismatch,
      '{{supplierName}} is offline because of a system mismatch',
      'Supplier offline due to version mismatch'
    );
    register.add(
      Id.supplierOfflineUnknownReasonSince,
      '{{supplierName}} is offline for an unknown reason since {{time}}. {{extraText}}',
      'Supplier offline for unknown reason message'
    );
    register.add(
      Id.supplierOfflineUpgradingMsg,
      "The {{glossary 'supplier'}} System is currently OFFLINE performing an Upgrade.\n\n This should normally take no more than one hour. \n\nPlease contact support ASAP to resolve this issue if it does not come back online after one hour.",
      'Supplier offline upgrading message'
    );
    register.add(Id.supplierOfflineTitle, "{{glossary 'supplier'}} OFFLINE", 'Supplier offline dialog title');
    register.add(
      Id.supplierOfflineMsg,
      "The activity you are trying to do is being blocked because the {{glossary 'supplier'}} system is OFFLINE.\n\n Please contact support ASAP to resolve this issue.\n\n We recommend closing down what you are doing until this is resolved \n\n",
      'Supplier offline message'
    );
    register.add(
      Id.supplierFrameConfigOfflineMsg,
      "The activity you are trying to do is being blocked because the {{glossary 'supplier'}} {{glossary 'frame'}} config could not be loaded. \n\n Please contact support ASAP to resolve this issue. \n\nWe recommend closing down what you are doing until this is resolved",
      'Supplier frame config offline message'
    );
    register.add(
      Id.frameConfigurationTitle,
      "{{glossary 'frame'}} configuration",
      // Neutral: shared by every failure that is a real fault (bad/unsupported config, script or
      // init failure) rather than the supplier actually being offline - must never read "OFFLINE".
      'Neutral dialog title for a frame-configuration fault (not an offline supplier)'
    );
    register.add(
      Id.tacApprovalNotSetMsg,
      "The {{glossary 'supplier'}} ({{supplier}}) Terms and Conditions have not been accepted by the {{glossary 'franchisee'}}. They will still need to accept the terms and conditions before using this feature",
      'Supplier TAC not approved message'
    );
  }
}
